import { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { Search, RefreshCw, ScrollText, Clock, CheckCircle, XCircle } from 'lucide-react';

interface PostbackLog {
    id: string;
    timestamp: any;
    request_ip: string;
    request_body: any;
    click_id: string;
    product_id: string;
    affiliate_id?: string;
    result: 'success' | 'error';
    error_code?: string;
    error_message?: string;
    conversion_id?: string;
    commission_calculated?: number;
    commission_source?: string;
    processing_time_ms: number;
}

export default function AdminPostbackLogs() {
    const [logs, setLogs] = useState<PostbackLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState<'all' | 'success' | 'error'>('all');
    const [expandedLog, setExpandedLog] = useState<string | null>(null);

    useEffect(() => { fetchLogs(); }, [filter]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const logsQuery = filter === 'all'
                ? query(collection(db, 'postback_logs'), orderBy('timestamp', 'desc'), limit(100))
                : query(collection(db, 'postback_logs'), where('result', '==', filter), orderBy('timestamp', 'desc'), limit(100));

            const snapshot = await getDocs(logsQuery);
            setLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as PostbackLog[]);
        } catch (error) {
            console.error('Error fetching logs:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (ts: any) => ts?.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) || '-';

    const filteredLogs = logs.filter(log => {
        if (!searchTerm) return true;
        const t = searchTerm.toLowerCase();
        return log.click_id?.toLowerCase().includes(t) || log.affiliate_id?.toLowerCase().includes(t) || log.product_id?.toLowerCase().includes(t);
    });

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-white">Postback Logs</h1>
                    <p className="text-gray-400">Debug conversion tracking</p>
                </div>
                <button onClick={fetchLogs} disabled={loading} className="btn-secondary flex items-center gap-2">
                    <RefreshCw size={20} className={loading ? 'animate-spin' : ''} /> Refresh
                </button>
            </div>

            <div className="card flex gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input type="text" placeholder="Search by click_id, affiliate_id..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="input-field pl-12" />
                </div>
                <select value={filter} onChange={(e) => setFilter(e.target.value as any)} className="input-field w-auto">
                    <option value="all">All</option>
                    <option value="success">Success</option>
                    <option value="error">Errors</option>
                </select>
            </div>

            <div className="card">
                {loading ? (
                    <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" /></div>
                ) : filteredLogs.length === 0 ? (
                    <div className="text-center py-12"><ScrollText className="mx-auto text-gray-500 mb-4" size={48} /><p className="text-gray-400">No logs found</p></div>
                ) : (
                    <div className="space-y-2">
                        {filteredLogs.map((log) => (
                            <div key={log.id} className={`p-4 rounded-xl border cursor-pointer ${log.result === 'success' ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`} onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-3">
                                        {log.result === 'success' ? <CheckCircle className="text-emerald-400" size={20} /> : <XCircle className="text-red-400" size={20} />}
                                        <span className="text-white font-mono text-sm">{log.click_id?.substring(0, 12)}...</span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                        {log.commission_calculated && <span className="text-emerald-400">€{log.commission_calculated.toFixed(2)}</span>}
                                        {log.error_code && <span className="text-red-400">{log.error_code}</span>}
                                        <span className="text-gray-500"><Clock size={14} className="inline mr-1" />{log.processing_time_ms}ms</span>
                                        <span className="text-gray-400">{formatDate(log.timestamp)}</span>
                                    </div>
                                </div>
                                {expandedLog === log.id && (
                                    <div className="mt-4 pt-4 border-t border-white/10 text-sm space-y-2">
                                        <p><span className="text-gray-500">Click ID:</span> <span className="text-white font-mono">{log.click_id}</span></p>
                                        <p><span className="text-gray-500">Affiliate:</span> <span className="text-white">{log.affiliate_id || '-'}</span></p>
                                        <p><span className="text-gray-500">Product:</span> <span className="text-white">{log.product_id}</span></p>
                                        <p><span className="text-gray-500">IP:</span> <span className="text-white">{log.request_ip}</span></p>
                                        {log.commission_source && <p><span className="text-gray-500">Source:</span> <span className="text-primary-400">{log.commission_source}</span></p>}
                                        {log.error_message && <p className="text-red-400">{log.error_message}</p>}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
